import React, { useState } from 'react';
import { SkillConfig } from '../../types/onboarding';
import { Image } from '../../../ui/Image';
import { useCreateSkill, useSkill } from '../../apis/skill-registry';
import { Button } from '../../components';

import { useTranslation } from 'react-i18next';

type VantaAccessLevel = 'read' | 'readWrite';

interface VantaSkillSetupProps {
  skill: SkillConfig;
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const VantaSkillSetup: React.FC<VantaSkillSetupProps> = ({
  skill,
  onComplete,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const [clientId, setClientId] = useState(
    (skill.config?.clientId as string) ?? '',
  );
  const [clientSecret, setClientSecret] = useState(
    (skill.config?.clientSecret as string) ?? '',
  );
  const [accessChoice, setAccessChoice] = useState<VantaAccessLevel | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: enableSkill, isPending: isEnabling } = useCreateSkill();
  const { data: existingSkill } = useSkill(skill.id, skill.enabled);
  // Skills saved before the choice existed have no access level: read only.
  const accessLevel: VantaAccessLevel =
    accessChoice ??
    (existingSkill?.config?.accessLevel === 'readWrite' ? 'readWrite' : 'read');

  const handleSubmit = async () => {
    if (isDisabled) {
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await enableSkill({
        skillType: skill.id,
        enabled: true,
        config: { accessLevel },
        secrets: {
          clientId,
          clientSecret,
        },
      });
      await onComplete();
    } catch (error) {
      console.error('Failed to setup Vanta skill:', error);
      setError(t('failedToConfigureVantaPleaseCheckYour'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isReconfiguring = skill.enabled;

  const isDisabled =
    isLoading ||
    isEnabling ||
    isSubmitting ||
    !clientId?.trim() ||
    !clientSecret?.trim();

  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
          {t('vanta')}
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 2. Logo */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-blue-50 blur-xl dark:bg-blue-950/10" />
            {skill.image ? (
              <Image
                src={skill.image.src}
                alt={skill.image.alt}
                className="relative h-20 rounded-xl object-cover"
              />
            ) : (
              <div className="relative flex h-20 w-20 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-600/20">
                <span className="text-4xl leading-tight">{skill.icon}</span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Short Description */}
        <div className="mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('iSpotComplianceGapsExplainWhatMissing')}
          </p>
        </div>

        {/* 4. Learn More Link */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">
            <a
              href="https://docs.pleri.ai/tools/vanta"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-700"
            >
              {t('clickHereToLearnMore')}
            </a>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* 5. Form Fields */}
        <div className="mb-8 space-y-4">
          <div className="mx-auto max-w-md">
            <label
              htmlFor="client-id"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('clientId')}
            </label>
            <input
              id="client-id"
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={t('enterYourVantaClientId')}
              className="h-10 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600"
              disabled={isLoading || isEnabling || isSubmitting}
            />
          </div>

          <div className="mx-auto max-w-md">
            <label
              htmlFor="client-secret"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('clientSecret')}
            </label>
            <input
              id="client-secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={t('enterYourVantaClientSecret')}
              className="h-10 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600"
              disabled={isLoading || isEnabling || isSubmitting}
            />
          </div>

          <fieldset className="mx-auto max-w-md">
            <legend className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('whatPleriCanDoInVanta')}
            </legend>
            <div className="space-y-3">
              {(
                [
                  {
                    value: 'read',
                    label: t('vantaReadOnly'),
                    description: t('vantaReadOnlyDescription'),
                  },
                  {
                    value: 'readWrite',
                    label: t('vantaReadWrite'),
                    description: t('vantaReadWriteDescription'),
                  },
                ] as const
              ).map(({ value, label, description }) => (
                <label
                  key={value}
                  className="flex cursor-pointer items-start gap-3"
                >
                  <input
                    type="radio"
                    name="vanta-access-level"
                    value={value}
                    checked={accessLevel === value}
                    onChange={() => setAccessChoice(value)}
                    disabled={isLoading || isEnabling || isSubmitting}
                    className="text-action-primary focus:ring-action-primary mt-0.5 h-4 w-4"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {label}
                    </span>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        {/* 6. Call to Action Button */}
        <div className="mb-6 flex flex-col items-center">
          <Button
            onClick={handleSubmit}
            disabled={isDisabled}
            className={`group relative flex w-full items-center justify-center rounded-lg px-8 py-3 text-sm font-medium transition-all ${
              isDisabled
                ? 'cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                : ''
            }`}
          >
            <span>
              {isReconfiguring ? t('updateConfiguration') : t('activateVanta')}
            </span>
          </Button>
        </div>

        {/* 7. Cancel or Go Back Button */}
        <div className="flex flex-col items-center">
          <button
            onClick={onCancel}
            disabled={isLoading || isEnabling || isSubmitting}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            {t('common:cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};
